import { stripUnderline } from './richText';

export const MOCK_TYPES = ['ubt', 'quiz', 'cbt', 'live'] as const;
export const TIERS = ['free', 'pro_medium'] as const;

export interface MockTestForm {
  title: { en: string; ne: string };
  description: { en: string; ne: string };
  type: string;
  tier: string;
  readingQuestions: Array<{ questionId: string; order: number; setId?: string }>;
  listeningQuestions: Array<{ questionId: string; order: number; setId?: string }>;
  questionSets: Array<{ id: string; title: string; section: 'reading' | 'listening' }>;
  timeLimitMinutes: number;
  readingQuestionCount: number;
  listeningQuestionCount: number;
  passingMarks: number;
  thumbnailUrl: string;
  isActive: boolean;
}

/** Minimal shape getQuestionLabel needs — QuestionItem (defined in MockTestsPage.tsx) satisfies this structurally. */
interface QuestionLabelSource {
  name?: string;
  ubtPrompt?: { stem?: string };
  question?: { text?: string; imageUrl?: string; audioUrl?: string };
}

export type PickerSection = 'readingQuestions' | 'listeningQuestions';
export type QuestionSort = 'newest' | 'oldest' | 'difficulty';

export type QuestionAssignment = { questionId: string; order: number; setId?: string };

export const emptyForm = (): MockTestForm => ({
  title: { en: '', ne: '' },
  description: { en: '', ne: '' },
  type: 'ubt',
  tier: 'free',
  readingQuestions: [],
  listeningQuestions: [],
  questionSets: [],
  timeLimitMinutes: 50,
  readingQuestionCount: 20,
  listeningQuestionCount: 20,
  passingMarks: 24,
  thumbnailUrl: '',
  isActive: true,
});

export function normalizeAssignments(assignments: QuestionAssignment[]): QuestionAssignment[] {
  return assignments.map((assignment, index) => ({ ...assignment, order: index + 1 }));
}

export function reorderAssignments(
  assignments: QuestionAssignment[],
  fromIndex: number,
  toIndex: number,
): QuestionAssignment[] {
  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= assignments.length ||
    toIndex >= assignments.length
  ) {
    return normalizeAssignments(assignments);
  }

  const next = [...assignments];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return normalizeAssignments(next);
}

export function toPayload(f: MockTestForm) {
  return {
    title: { en: f.title.en, ...(f.title.ne && { ne: f.title.ne }) },
    ...(f.description.en && {
      description: { en: f.description.en, ...(f.description.ne && { ne: f.description.ne }) },
    }),
    type: f.type,
    tier: f.tier,
    readingQuestions: f.readingQuestions,
    listeningQuestions: f.listeningQuestions,
    ...(f.questionSets.length > 0 && { questionSets: f.questionSets }),
    timeLimitMinutes: f.timeLimitMinutes,
    readingQuestionCount: f.readingQuestionCount,
    listeningQuestionCount: f.listeningQuestionCount,
    totalMarks: f.readingQuestionCount + f.listeningQuestionCount,
    passingMarks: f.passingMarks,
    ...(f.thumbnailUrl && { thumbnailUrl: f.thumbnailUrl }),
    isActive: f.isActive,
  };
}

export function getQuestionLabel(question: QuestionLabelSource) {
  const promptLabel = question.ubtPrompt?.stem ? stripUnderline(question.ubtPrompt.stem) : '';

  return (
    question.name ||
    promptLabel ||
    stripUnderline(question.question?.text) ||
    question.question?.imageUrl ||
    question.question?.audioUrl ||
    'Untitled question'
  );
}

export function createSetId(section: 'reading' | 'listening') {
  return `${section}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/**
 * Compares each section's assigned question count against its declared
 * target and returns a human-readable mismatch message, or null if both
 * sections match. Used to block save before the server round-trip.
 */
export function countMismatch(f: MockTestForm): string | null {
  if (f.readingQuestions.length !== f.readingQuestionCount) {
    return `Reading has ${f.readingQuestions.length} question${f.readingQuestions.length === 1 ? '' : 's'} assigned but the target is ${f.readingQuestionCount}.`;
  }
  if (f.listeningQuestions.length !== f.listeningQuestionCount) {
    return `Listening has ${f.listeningQuestions.length} question${f.listeningQuestions.length === 1 ? '' : 's'} assigned but the target is ${f.listeningQuestionCount}.`;
  }
  return null;
}
